import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, X, Layers } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '../../lib/api';
import { SERVICE_ICONS, getServiceIcon, formatServicePrice } from '../../lib/siteServices';

const EMPTY = {
  title: '', description: '', icon: 'Sparkles', order: 0, features: [],
  price: '', price_label: '', visible: true, action: 'contact', href: ''
};

const ACTIONS = [
  { value: 'contact', label: 'Contact (appel / WhatsApp)' },
  { value: 'billetterie', label: 'Formulaire billet & hôtel' },
  { value: 'link', label: "Lien vers une page" }
];

const PAGES = ['/visa', '/services/assurance', '/services/plan-touristique', '/services/passeport', '/conciergerie', '/offres', '/contact'];

const IconPicker = ({ value, onChange }) => (
  <div className="grid grid-cols-10 gap-1.5" data-testid="service-icon-picker">
    {Object.entries(SERVICE_ICONS).map(([name, Icon]) => (
      <button
        type="button" key={name} title={name} onClick={() => onChange(name)}
        className={`h-9 rounded-lg flex items-center justify-center border transition-colors ${value === name ? 'border-sky-400 bg-sky-500/20 text-sky-300' : 'border-white/10 text-gray-400 hover:text-white hover:bg-white/5'}`}
        data-testid={`icon-option-${name}`}
      >
        <Icon className="w-4 h-4" />
      </button>
    ))}
  </div>
);

const FeaturesEditor = ({ features, onChange }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    if (!draft.trim()) return;
    onChange([...features, draft.trim()]);
    setDraft('');
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Ex : Traitement en 48h" className="bg-white/5 border-white/10"
          data-testid="service-feature-input"
        />
        <Button type="button" variant="outline" className="border-white/10" onClick={add} data-testid="service-feature-add-btn">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {features.map((f, i) => (
        <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white/5 text-sm" data-testid={`service-feature-item-${i}`}>
          <span>{f}</span>
          <button type="button" onClick={() => onChange(features.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-400" data-testid={`service-feature-remove-${i}`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
};

const ServiceCard = ({ service, onEdit, onDelete, onToggle }) => {
  const Icon = getServiceIcon(service.icon);
  const price = formatServicePrice(service);
  return (
    <Card className={`bg-[#0F172A] border-white/10 ${!service.visible ? 'opacity-60' : ''}`} data-testid={`admin-service-card-${service.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-sky-500/10 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-sky-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-white">{service.title}</h3>
              {!service.visible && <Badge className="bg-gray-500/20 text-gray-400">Masqué</Badge>}
            </div>
            <p className="text-xs text-gray-500">Ordre : {service.order}{service.features?.length ? ` · ${service.features.length} points clés` : ''}</p>
          </div>
        </div>
        <p className="text-sm text-gray-400 line-clamp-2">{service.description}</p>
        {price && <p className="text-sky-400 font-bold">{price}</p>}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1 border-white/10 text-gray-300 hover:text-white" onClick={() => onEdit(service)} data-testid={`edit-service-${service.id}`}>
            <Edit className="w-4 h-4 mr-1" /> Modifier
          </Button>
          <Button variant="outline" size="sm" className="border-white/10 text-gray-300" onClick={() => onToggle(service)} title={service.visible ? 'Masquer' : 'Afficher'} data-testid={`toggle-service-${service.id}`}>
            {service.visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button variant="outline" size="sm" className="border-red-500/50 text-red-400 hover:bg-red-500/10" onClick={() => onDelete(service)} data-testid={`delete-service-${service.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const toPayload = (f) => ({
  ...f,
  order: Number(f.order) || 0,
  price: f.price === '' || f.price == null ? null : Number(f.price),
  price_label: f.price_label || null,
  href: f.action === 'link' ? f.href || null : null
});

export default function SiteServicesManagementPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    try {
      const r = await api.get('/site-services/admin');
      setServices(r.data);
    } catch {
      toast.error('Erreur de chargement des services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY, order: services.length });
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({ ...EMPTY, ...s, price: s.price ?? '', price_label: s.price_label || '', href: s.href || '' });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Le titre est requis');
    if (form.action === 'link' && !form.href) return toast.error('Choisissez la page de destination');
    setSaving(true);
    try {
      const payload = toPayload(form);
      delete payload.id;
      if (editingId) await api.put(`/site-services/${editingId}`, payload);
      else await api.post('/site-services', payload);
      toast.success(editingId ? 'Service mis à jour' : 'Service créé');
      setForm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail?.toString() || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (s) => {
    const { id, ...rest } = s;
    try {
      await api.put(`/site-services/${id}`, { ...rest, visible: !s.visible });
      toast.success(s.visible ? 'Service masqué du site' : 'Service affiché sur le site');
      load();
    } catch {
      toast.error('Erreur');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/site-services/${toDelete.id}`);
      toast.success('Service supprimé');
      setToDelete(null);
      load();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) return <div className="text-gray-400" data-testid="services-loading">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="site-services-management-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestion des Services</h2>
          <p className="text-gray-400 text-sm">{services.length} services · affichés sur l'accueil et la page Services</p>
        </div>
        <Button className="bg-sky-500 hover:bg-sky-600" onClick={openCreate} data-testid="create-service-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau Service
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((s) => (
          <ServiceCard key={s.id} service={s} onEdit={openEdit} onDelete={setToDelete} onToggle={toggle} />
        ))}
        {services.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-400" data-testid="services-empty">
            <Layers className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucun service</p>
          </div>
        )}
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0F172A] border-white/10 text-white" data-testid="service-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifier le service' : 'Nouveau service'}</DialogTitle>
          </DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input value={form.title} onChange={(e) => set('title', e.target.value)} className="bg-white/5 border-white/10" data-testid="service-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={3} className="bg-white/5 border-white/10" data-testid="service-description-input" />
              </div>
              <div className="space-y-2">
                <Label>Icône</Label>
                <IconPicker value={form.icon} onChange={(v) => set('icon', v)} />
              </div>
              <div className="space-y-2">
                <Label>Points clés</Label>
                <FeaturesEditor features={form.features} onChange={(v) => set('features', v)} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Mention prix</Label>
                  <Input value={form.price_label} onChange={(e) => set('price_label', e.target.value)} placeholder="À partir de" className="bg-white/5 border-white/10" data-testid="service-price-label-input" />
                </div>
                <div className="space-y-2">
                  <Label>Prix indicatif ($)</Label>
                  <Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => set('price', e.target.value)} placeholder="Vide = aucun" className="bg-white/5 border-white/10" data-testid="service-price-input" />
                </div>
                <div className="space-y-2">
                  <Label>Ordre d'affichage</Label>
                  <Input type="number" value={form.order} onChange={(e) => set('order', e.target.value)} className="bg-white/5 border-white/10" data-testid="service-order-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Action du bouton</Label>
                  <Select value={form.action} onValueChange={(v) => set('action', v)}>
                    <SelectTrigger className="bg-white/5 border-white/10" data-testid="service-action-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-white/10 text-white">
                      {ACTIONS.map((a) => <SelectItem key={a.value} value={a.value} data-testid={`service-action-${a.value}`}>{a.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.action === 'link' && (
                  <div className="space-y-2">
                    <Label>Page de destination</Label>
                    <Select value={form.href || undefined} onValueChange={(v) => set('href', v)}>
                      <SelectTrigger className="bg-white/5 border-white/10" data-testid="service-href-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                      <SelectContent className="bg-[#1E293B] border-white/10 text-white">
                        {PAGES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.visible} onCheckedChange={(v) => set('visible', v)} data-testid="service-visible-switch" />
                <Label>Visible sur le site</Label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" className="border-white/10" onClick={() => setForm(null)} data-testid="service-cancel-btn">Annuler</Button>
                <Button type="submit" disabled={saving} className="bg-sky-500 hover:bg-sky-600" data-testid="service-save-btn">
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#0F172A] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer « {toDelete?.title} » ?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">Cette action est définitive. Pour le retirer temporairement, utilisez plutôt « Masquer ».</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10" data-testid="service-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={confirmDelete} data-testid="service-delete-confirm">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
